import { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, setAuthLostHandler, type WorkflowDto } from "./lib/api";
import type { MeDto, MeUser } from "./lib/auth";
import { AppHeader } from "./components/AppHeader";
import { ErrorBox, Spinner } from "./components/ui";
import {
  AuthFrame,
  Anmeldung,
  Konto,
  PasswortNeu,
  Pflichtschritt,
  useLogout,
  useMe,
} from "./screens/Anmeldung";
import { ME_KEY } from "./lib/session";
import { Dashboard } from "./screens/Dashboard";
import { BusinessDays } from "./screens/BusinessDays";
import { BusinessDayDetail } from "./screens/BusinessDayDetail";
import { CashReview } from "./screens/CashReview";
import { Import } from "./screens/Import";
import { MonthlyOverview } from "./screens/MonthlyOverview";
import { Payouts } from "./screens/Payouts";
import { MasterData } from "./screens/MasterData";
import { AuditLog } from "./screens/AuditLog";
import { Aushang } from "./screens/Aushang";
import { Hilfe } from "./screens/Hilfe";
import { Auswertung } from "./screens/Auswertung";

/**
 * Vor der App steht die Anmeldung. Wer angemeldet ist, sagt die API; solange
 * etwas fällig ist (eigenes Passwort, zweiter Faktor), gibt es nur diesen
 * einen Schritt und nichts daneben.
 */
export function App() {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const me = useMe();

  // Läuft die Sitzung ab, antwortet die nächste Anfrage mit 401 — dann zurück
  // zur Anmeldung statt einer Fehlermeldung mitten in der Maske.
  useEffect(() => {
    setAuthLostHandler(() => void queryClient.invalidateQueries({ queryKey: ME_KEY }));
    return () => setAuthLostHandler(null);
  }, [queryClient]);

  // Der Link aus der Mail funktioniert angemeldet wie abgemeldet.
  if (pathname === "/passwort-neu") return <PasswortNeu />;
  if (me.isLoading) return <AuthFrame><Spinner label="Prüft die Anmeldung" /></AuthFrame>;
  if (me.error || !me.data) {
    return (
      <AuthFrame>
        <ErrorBox error={me.error ?? new Error("Keine Verbindung zur API")} />
      </AuthFrame>
    );
  }

  const { user, mode } = me.data;
  if (!user) {
    return <Anmeldung mailReset={me.data.mailReset ?? false} microsoft={me.data.microsoft ?? false} />;
  }
  if (user.pending) return <Pflichtschritt me={{ mode, user }} />;
  return <Shell me={{ mode, user }} />;
}

function Shell({ me }: { me: MeDto & { user: MeUser } }) {
  const { user, mode } = me;
  const logout = useLogout(mode);

  // Liefert Zeitraum, nächsten Schritt und Zähler für den Kopf.
  const workflow = useQuery({
    queryKey: ["workflow"],
    queryFn: () => api.get<WorkflowDto>("/workflow"),
    refetchInterval: 60_000,
  });

  return (
    <div className="min-h-screen">
      <AppHeader
        user={user}
        mode={mode}
        workflow={workflow.data}
        onLogout={() => logout.mutate()}
        loggingOut={logout.isPending}
      />

      <main className="mx-auto max-w-[1400px] px-6 py-10">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/kassen-review" element={<CashReview />} />
          <Route path="/betriebstage" element={<BusinessDays />} />
          <Route path="/betriebstage/:date" element={<BusinessDayDetail />} />
          <Route path="/monat" element={<MonthlyOverview />} />
          <Route path="/auswertung" element={<Auswertung />} />
          <Route path="/auszahlungen" element={<Payouts />} />
          <Route path="/import" element={<Import />} />
          <Route path="/stammdaten" element={<MasterData />} />
          <Route path="/audit" element={<AuditLog />} />
          <Route path="/aushang" element={<Aushang />} />
          <Route path="/konto" element={<Konto me={me} />} />
          <Route path="/hilfe" element={<Hilfe />} />
          <Route
            path="*"
            element={<p className="text-sm text-muted">Diese Seite gibt es nicht.</p>}
          />
        </Routes>
      </main>
    </div>
  );
}
